import { debounce } from '@src/utils/functions/utils'

import {
  DEFAULT_DEBOUNCE_PERIOD,
  DEFAULT_EVENT_LIMIT,
  EVENT_PUBLISHER_DEBOUNCE_PERIOD,
} from './batched-analytics.constants'
import { BatchedAnalyticsStream } from './batched-analytics.types'

/**
 * __sortNode CONTRACT:
 *
 * __sortNode is an internal field that holds a live DOM node reference, used only to sort
 * a batch into render order before publishing. It is non-serializable and must NEVER reach
 * analytics subscribers or the wire.
 *
 * CRITICAL: Every subscriber callback must map events through a field-whitelist
 * (e.g., getProductDataForBatchedAnalytics) that drops non-tracked fields. A raw
 * `{...event}` spread or JSON.stringify() would leak a non-serializable DOM node and crash.
 *
 * Current state: All ~16 subscribers whitelist correctly. ✅
 * This is a footgun for future consumers, not a live bug — this comment prevents it.
 *
 * Regression guard: getProductDataForBatchedAnalytics.test.ts verifies __sortNode is
 * never in the published output.
 */
export const BATCHED_EVENT_SORT_NODE = '__sortNode'

const sortEventsByRenderOrder = <T extends Record<string, any>>(events: T[]): T[] => {
  if (!events.some(event => event?.[BATCHED_EVENT_SORT_NODE])) return events
  return [...events].sort((a, b) => {
    const nodeA: Node | undefined = a?.[BATCHED_EVENT_SORT_NODE]
    const nodeB: Node | undefined = b?.[BATCHED_EVENT_SORT_NODE]

    // Both have nodes: sort by DOM position
    if (nodeA && nodeB) {
      if (nodeA === nodeB) return 0
      return nodeA.compareDocumentPosition(nodeB) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    }

    // Only nodeA has a node: move before non-node events
    if (nodeA && !nodeB) return -1

    // Only nodeB has a node: move before non-node events
    if (!nodeA && nodeB) return 1

    // Neither has a node: preserve insertion order
    return 0
  })
}

export class BatchedAnalytics {
  private readonly batchedAnalyticsStream: BatchedAnalyticsStream

  constructor({ batchedAnalyticsStream }: { batchedAnalyticsStream: BatchedAnalyticsStream }) {
    this.batchedAnalyticsStream = batchedAnalyticsStream
  }

  initializeBatchedAnalyticsEvent({
    tracker,
    debouncePeriod,
    eventLimit,
    eventList,
  }: {
    tracker: string
    debouncePeriod?: number
    eventLimit?: number
    eventList?: any
  }) {
    if (tracker && !this.batchedAnalyticsStream[tracker]) {
      this.batchedAnalyticsStream[tracker] = {
        tracker,
        debouncePeriod: debouncePeriod ?? DEFAULT_DEBOUNCE_PERIOD,
        eventLimit: eventLimit ?? DEFAULT_EVENT_LIMIT,
        eventList: eventList || [],
        subscribers: {},
        debouncedPublisher: debounce(args => {
          this.publishEventsToAnalytics(args)
        }, EVENT_PUBLISHER_DEBOUNCE_PERIOD),
        isPublishing: false,
      }
    }
  }

  pushEventToBatchedAnalyticsStream({
    tracker,
    event,
    retryCount = 0,
  }: {
    tracker: string
    event: any
    retryCount?: number
  }) {
    if (tracker) {
      if (this.batchedAnalyticsStream[tracker]) {
        const trackerForPublish = this.batchedAnalyticsStream[tracker]
        trackerForPublish.eventList.push(event)
        clearTimeout(trackerForPublish.timeoutId)
        trackerForPublish.timeoutId = undefined

        // Publishing with length check
        const currentEventListLength = trackerForPublish.eventList.length
        if (
          trackerForPublish.eventLimit &&
          currentEventListLength == trackerForPublish.eventLimit &&
          !trackerForPublish.isPublishing
        ) {
          trackerForPublish.isPublishing = true
          trackerForPublish.debouncedPublisher({
            tracker,
            batchedAnalyticsStream: this.batchedAnalyticsStream,
          })
        } else if (trackerForPublish.debouncePeriod && !trackerForPublish.isPublishing) {
          // Publishing with debouncing
          // trackerForPublish.isPublishing
          trackerForPublish.timeoutId = setTimeout(() => {
            trackerForPublish.debouncedPublisher({
              tracker,
              batchedAnalyticsStream: this.batchedAnalyticsStream,
            })
          }, trackerForPublish.debouncePeriod)
        }
      } else {
        if (retryCount) {
          this.initializeBatchedAnalyticsEvent({ tracker })
          this.pushEventToBatchedAnalyticsStream({ tracker, event, retryCount: retryCount - 1 })
        } else {
          throw new Error(`Tracker must be initialized before event is added : ${tracker}`)
        }
      }
    }
  }

  publishEventsToAnalytics({
    tracker,
    batchedAnalyticsStream,
  }: {
    tracker: string
    batchedAnalyticsStream: BatchedAnalyticsStream
  }) {
    if (tracker) {
      const trackerForPublish = batchedAnalyticsStream[tracker]
      if (trackerForPublish) {
        const currentEvents = sortEventsByRenderOrder([...trackerForPublish.eventList])
        if (batchedAnalyticsStream[tracker]) batchedAnalyticsStream[tracker].eventList = []
        Object.keys(trackerForPublish.subscribers).forEach(subscriberId => {
          trackerForPublish.subscribers[subscriberId]?.callback({
            tracker,
            events: currentEvents,
          })
        })
        trackerForPublish.isPublishing = false
      } else {
        throw new Error(`Tracker must be initialized before events are published : ${tracker}`)
      }
    }
  }

  trackBatchedAnalytics({
    id,
    tracker,
    callback,
  }: {
    id: string
    tracker: string
    callback: ({ tracker, events }: { tracker: string; events: any[] }) => void
  }) {
    if (this.batchedAnalyticsStream[tracker]) {
      const currentSubscribers = this.batchedAnalyticsStream[tracker].subscribers
      if (!currentSubscribers[id]) {
        this.batchedAnalyticsStream[tracker].subscribers[id] = { id, callback }
      }
    } else {
      throw new Error(`Cannot add subscriber for id ${id}. Tracker is not initialised yet for : ${tracker}`)
    }
  }
}

const batchedAnalytics = new BatchedAnalytics({ batchedAnalyticsStream: {} })

export default batchedAnalytics
